<template>
  <div v-if="readonly">
    <div class="d-flex align-center">
      <p class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-1">
        {{ label }}
      </p>
      <v-btn
        v-if="readOnlyValues.length > 30"
        icon
        x-small
        color="blue-grey darken-1"
        class="ms-2"
        :ripple="false"
        @click="showChips = !showChips"
      >
        <v-icon size="16"> {{ readonlyIcon }}</v-icon>
      </v-btn>
    </div>

    <v-slide-y-transition>
      <div v-if="showChips">
        <template v-for="item in readOnlyValues">
          <v-chip
            v-if="item.value"
            :key="item.value"
            color="readonly-filter-chip blue-grey lighten-3 blue-grey--text text--darken-3"
            class="me-1 mb-1"
            small
            :ripple="false"
            >{{ item.text }}</v-chip
          >
        </template>
      </div>
    </v-slide-y-transition>
  </div>

  <div
    v-else
    class="selector-filter-container"
    :style="{ width: `${fixedWidth}px` }"
    :data-tourid="`filter-select-${id}`"
  >
    <v-autocomplete
      ref="autocomplete"
      class="caption white"
      append-icon="mdi-chevron-down"
      dense
      hide-details
      open-on-clear
      outlined
      :data-testid="`selectorFilter-autocomplete-${id}`"
      :disabled="isLoading"
      :filter="filterItem"
      :items="options"
      :loading="isLoading"
      :menu-props="{
        offsetY: true,
        maxWidth: 430,
        maxHeight: 312,
        bottom: true,
      }"
      :multiple="multiselect"
      :no-data-text="$t('error.no_data_available')"
      :no-filter="noFilter"
      :placeholder="$t('menu.selection.select_an_option')"
      :search-input.sync="searchTerm"
      :value="value"
      @blur="handleMenuStateChange(false)"
      @change="onChangeFilter($event)"
      @focus="handleMenuStateChange(true)"
      @keydown.esc="handleEscapeKey"
    >
      <template v-slot:label>
        <div class="caption white px-1" data-form-type="other">
          {{ label }}
        </div>
      </template>

      <template v-slot:prepend-item>
        <v-card
          :loading="isSearching"
          elevation="0"
          class="cursor-pointer pl-3 mt-n2"
          @click="handleClearFilterOptions"
        >
          <p
            v-if="allowClear"
            class="pt-3 caption font-weight-500 blue--text clear-button"
          >
            {{ $t(multiselect ? 'common.clear_all' : 'common.clear') }}
          </p>
        </v-card>

        <div v-if="options.length > 10 || noFilter" class="mx-4 my-2">
          <v-text-field
            hide-details
            single-line
            prepend-icon="search"
            class="d-flex align-center search-inside-filter pt-0 mt-0"
            clearable
            :value="searchTerm"
            @input="handleSearchInFilterOptions"
          >
            <template v-slot:label>
              <span class="body-3">{{ $t('common.search') }}</span>
            </template>
          </v-text-field>
        </div>
      </template>

      <template v-slot:selection="{ item, index }">
        <div v-if="multiselect" class="d-flex">
          <p
            v-if="index < 2"
            class="selection-item-container ma-0 pa-0"
            :style="{ 'max-width': contentWidth }"
          >
            {{ truncateSelectedItemText(item) }}
          </p>

          <span v-if="index === 0 && value.length > 1" class="ml-0 mr-1"
            >,
          </span>

          <v-tooltip
            v-if="index === 2"
            bottom
            max-width="400px"
            :style="{ 'max-width': contentWidth }"
          >
            <template v-slot:activator="{ on }">
              <v-chip v-on="on" small color="blue-grey lighten-3">
                + {{ tooltipValues.length - 2 }}
              </v-chip>
            </template>
            <ul class="ma-0 pa-0 ms-2">
              <div v-for="(option, index) in tooltipValues" :key="option.value">
                <li v-if="index > 1" class="ma-0 pa-0">
                  {{ option.text }}
                </li>
              </div>
            </ul>
          </v-tooltip>
        </div>

        <div v-else>
          <p class="selection-item-container ma-0 pa-0">
            {{ item.text }}
          </p>
        </div>
      </template>

      <template v-slot:item="data">
        <template v-if="multiselect">
          <v-checkbox
            hide-details
            class="ma-0 pa-0 py-1 ml-n2"
            color="blue"
            :key="data.item.value"
            :ripple="false"
            :value="data.attrs.inputValue"
            :input-value="data.attrs.inputValue"
          >
            <template v-slot:label>
              <v-list-item-content class="mx-0 px-0 ms-2">
                <v-list-item-title
                  class="d-flex mx-0 px-0 blue-grey--text text--darken-3 mb-1"
                  >{{ data.item.text }}</v-list-item-title
                >

                <v-list-item-subtitle
                  v-if="data.item.label"
                  class="font-size-12 primary--text d-flex align-center"
                >
                  {{ data.item.label }}
                </v-list-item-subtitle>

                <v-list-item-subtitle
                  v-if="data.item.subtitle"
                  class="font-size-12 blue-grey--text d-flex align-center"
                >
                  <v-icon
                    color="blue-grey"
                    size="12"
                    class="me-1"
                    v-if="data.item.subtitleIcon"
                    >{{ data.item.subtitleIcon }}</v-icon
                  >
                  {{ data.item.subtitle }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-checkbox>
        </template>
        <v-list-item-content v-else class="ml-n1">
          <v-list-item-title
            class="mx-0 px-0 blue-grey--text text--darken-3 mb-1"
            >{{ data.item.text }}</v-list-item-title
          >
          <v-list-item-subtitle
            class="font-size-12 blue-grey--text d-flex align-center"
          >
            <v-icon
              color="blue-grey"
              size="12"
              class="me-1"
              v-if="data.item.subtitleIcon"
              >{{ data.item.subtitleIcon }}</v-icon
            >
            {{ data.item.subtitle }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import _ from 'lodash'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'
import {
  finishDeferredInteraction as resolveDeferredInteractionResult,
  startDeferredInteraction as createDeferredInteractionState
} from './deferredInteraction'

const DIACRITICAL_ACCENT_REGEX = /[\u0300-\u036f]/g

export default {
  name: 'SelectorFilter',

  mixins: [trackingMixin],

  props: {
    readonly: Boolean,
    label: String,
    id: String,
    options: { type: Array, default: () => [] },
    value: [String, Array],
    multiselect: Boolean,
    allowClear: { type: Boolean, default: false },
    isLoading: Boolean,
    isSearching: Boolean,
    noFilter: { type: Boolean, default: false },
    width: { type: String, default: null },
    fixedWidth: Number,
    emitAutoApplyMetadata: Boolean,
  },

  data() {
    return {
      interactionSequence: 0,
      menuCurrentValue: null,
      menuInitialValue: null,
      menuInteractionId: null,
      menuStateWatcher: null,
      searchTerm: '',
      showChips: true,
    }
  },

  watch: {
    searchTerm(newVal, oldVal) {
      if (!newVal && !oldVal) return
      this.handleSearchInFilterOptions(newVal)
    },
  },

  beforeDestroy() {
    this.menuStateWatcher?.()
  },

  computed: {
    readonlyIcon() {
      return this.showChips ? 'mdi-chevron-up' : 'mdi-chevron-down'
    },

    tooltipValues() {
      const isArrayValues = _.isArray(this.value)
      return isArrayValues
        ? this.value?.map(value => {
            return this.options.find(option => option.value === value)
          })
        : []
    },

    readOnlyValues() {
      const isArrayValues = _.isArray(this.value)

      return isArrayValues
        ? this.value?.map(value => {
            return this.options.find(option => option.value === value)
          })
        : [
            {
              value: this.value,
              text:
                this.options?.find?.(option => option?.value === this.value)
                  ?.text ?? this.value,
            },
          ]
    },

    contentWidth() {
      const numberOfInnerItems = 3
      const extraSpace = 20

      return this.fixedWidth
        ? `${this.fixedWidth / numberOfInnerItems - extraSpace}px`
        : ''
    },
  },

  methods: {
    buildChangePayload(value, overrides = {}) {
      if (!this.emitAutoApplyMetadata) {
        return {
          key: this.id,
          value,
        }
      }

      return {
        key: this.id,
        value,
        ...overrides,
      }
    },

    emitFilterChange(value, overrides = {}) {
      this.$emit('change', this.buildChangePayload(value, overrides))
    },

    shouldDeferSelection() {
      return this.multiselect && this.emitAutoApplyMetadata
    },

    startDeferredInteraction() {
      const deferredState = createDeferredInteractionState({
        interactionSequence: this.interactionSequence,
        prefix: this.id,
        value: this.value ?? [],
      })

      this.interactionSequence = deferredState.interactionSequence
      this.menuCurrentValue = deferredState.menuCurrentValue
      this.menuInitialValue = deferredState.menuInitialValue
      this.menuInteractionId = deferredState.menuInteractionId
    },

    resetDeferredInteraction() {
      this.menuCurrentValue = null
      this.menuInitialValue = null
      this.menuInteractionId = null
    },

    ensureDeferredInteraction() {
      if (!this.menuInteractionId) this.startDeferredInteraction()
    },

    emitDeferredDraft(value) {
      this.ensureDeferredInteraction()
      this.menuCurrentValue = globalThis.structuredClone(value ?? [])
      this.emitFilterChange(value, {
        commitMode: 'deferredAfterBlur',
        source: 'user',
        shouldApply: false,
        interactionId: this.menuInteractionId,
      })
    },

    emitInstantSelection(value) {
      this.emitFilterChange(value, {
        commitMode: 'instant',
        source: 'user',
        shouldApply: true,
      })
    },

    finishDeferredInteraction() {
      if (!this.emitAutoApplyMetadata || !this.menuInteractionId) return

      const { didChange, finalValue } = resolveDeferredInteractionResult({
        fallbackValue: this.value ?? [],
        menuCurrentValue: this.menuCurrentValue,
        menuInitialValue: this.menuInitialValue,
      })

      if (!didChange) {
        this.resetDeferredInteraction()
        return
      }

      this.emitFilterChange(finalValue, {
        commitMode: 'deferredAfterBlur',
        source: 'user',
        shouldApply: true,
        interactionId: this.menuInteractionId,
      })

      this.resetDeferredInteraction()
    },

    handleMenuStateChange(isOpen) {
      if (!this.multiselect || !this.emitAutoApplyMetadata) return

      if (isOpen) {
        if (!this.menuInteractionId) this.startDeferredInteraction()
        this.ensureMenuStateWatcher()
        return
      }

      this.finishDeferredInteraction()
    },

    ensureMenuStateWatcher() {
      if (this.menuStateWatcher) return
      if (!this.$refs?.autocomplete) return

      this.menuStateWatcher = this.$refs.autocomplete.$watch(
        'isMenuActive',
        (isOpen, wasOpen) => {
          if (!_.isBoolean(isOpen) || isOpen === wasOpen) return
          this.handleMenuStateChange(isOpen)
        }
      )
    },

    handleEscapeKey() {
      if (!this.shouldDeferSelection()) return

      this.$nextTick(() => {
        if (!this.$refs?.autocomplete?.isMenuActive) {
          this.handleMenuStateChange(false)
        }
      })
    },

    onChangeFilter(value) {
      this.searchTerm = ''

      if (!this.shouldDeferSelection()) {
        this.emitInstantSelection(value)
        this.trackFilterActions('filters.changeFilterSelection', {
          name: this.label,
          value: this.value,
        })
        return
      }

      this.emitDeferredDraft(value)

      this.trackFilterActions('filters.changeFilterSelection', {
        name: this.label,
        value: this.value,
      })
    },

    handleClearFilterOptions() {
      const value = this.multiselect ? [] : null

      if (!this.shouldDeferSelection()) {
        this.emitInstantSelection(value)
        this.trackFilterActions('filters.clearAllSelection', {
          name: this.label,
        })
        return
      }

      this.emitDeferredDraft(value)

      this.trackFilterActions('filters.clearAllSelection', {
        name: this.label,
      })
    },

    handleSearchInFilterOptions(searchTerm) {
      this.searchTerm = searchTerm
      this.$emit('search', searchTerm)
      this.trackFilterActions('filters.searchInFilterOptions', {
        name: this.label,
        value: this.value,
        searchTerm,
      })
    },

    normalizeText(text) {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(DIACRITICAL_ACCENT_REGEX, '')
    },

    filterItem(_item, queryText, itemText) {
      const search = this.normalizeText(queryText)
      const text = this.normalizeText(itemText)
      return text.includes(search)
    },

    truncateSelectedItemText(item) {
      const maxLength = 10
      if (item.text.length <= maxLength) {
        return item.text
      }
      return `${item.text.slice(0, maxLength)}...`
    },
  },
}
</script>

<style lang="scss" scoped>
.clear-button:hover {
  opacity: 0.7;
}
</style>

<style lang="scss">
.selector-filter-container {
  position: relative;
  height: 32px !important;
  min-height: 32px !important;

  .v-input__control .v-input__slot {
    background-color: $color-white;
    height: 32px !important;
    min-height: 32px !important;

    fieldset legend {
      width: fit-content !important;
    }
    .v-select__slot {
      display: flex;
      align-items: center;
      .v-label {
        top: 8px;
        div {
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
      .v-label--active {
        top: 10px;
      }
      .v-select__selections:has(div) {
        input {
          width: 0px !important;
          min-width: 0px !important;
        }
      }
    }
    .v-input__append-inner {
      margin-top: 4px !important;
    }
  }

  .v-input:not(.v-input--is-disabled) .v-input__control .v-input__slot {
    cursor: pointer;
  }

  .v-input__append-outer {
    background-color: $color-blue-grey-lighten3;
    margin: 0 !important;
    height: 32px;
    display: flex;
    align-items: center;
  }

  .selection-item-container {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.search-inside-filter .v-input__control .v-input__slot::before,
.search-inside-filter .v-input__control .v-input__slot::after {
  border-style: hidden;
}
</style>
